import Phaser from 'phaser';
import { gameAudio } from '../audio/audio';
import { DEFAULT_GESTURES } from '../input/gestures';
import { CELL_W, PIXEL_FONT } from './pixelFont';

/**
 * The UI material (docs/ART_DIRECTION.md): navy panels with a white rule and a periwinkle inner
 * line, white/yellow text with an ink shadow. UI is navy; the world never is.
 */
export const UI = {
  text: 0xffffff,
  dim: 0xb9b2ff,
  accent: 0xffd23f,
  /** The current selection (lawn plates): orange, so it stands apart from yellow labels. */
  select: 0xff8a1f,
  danger: 0xff5a4a,
  good: 0x7ad04c,
  panel: 0x20184a,
  panelHi: 0x6a5fd0,
  rule: 0xffffff,
  panelAlpha: 0.95,
  shadow: 0x0b0820,
} as const;

/** Text scale: 1 = body (5×7 glyphs), 2 = headings, 3 = the title logo. Integers stay crisp. */
export type TextScale = 1 | 2 | 3;

export function label(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  scale: TextScale = 1,
  color: number = UI.text,
): Phaser.GameObjects.BitmapText {
  return (
    scene.add
      // RetroFont's native size is its cell width, so this renders at exactly 1×, 2× or 3×.
      .bitmapText(Math.round(x), Math.round(y), PIXEL_FONT, text, CELL_W * scale)
      .setTint(color)
      .setDropShadow(1, 1, UI.shadow, 0.8)
  );
}

/**
 * A tap on `obj`: a press that began on it and is released on it, having moved at most `maxMove`
 * game px. Over a lawn (the HUD), plain `pointerup` would also fire for a swipe that merely ends
 * on the button (owner, 2026-10-10: a boost going off while steering). The e2e harness emits a
 * bare 'pointerup' with no pointer, which counts as a tap.
 */
export function onTap(
  obj: Phaser.GameObjects.GameObject,
  fn: () => void,
  maxMove = Number.POSITIVE_INFINITY,
): void {
  let pressedAt = -1;
  obj.on('pointerdown', (p: Phaser.Input.Pointer) => (pressedAt = p.downTime));
  obj.on('pointerup', (p?: Phaser.Input.Pointer) => {
    if (p) {
      const began = pressedAt === p.downTime;
      pressedAt = -1;
      if (!began || Math.hypot(p.upX - p.downX, p.upY - p.downY) > maxMove) return;
    }
    fn();
  });
}

/**
 * How far a finger may slide on a HUD button and still press it (game px): a tap's, so a touch
 * there is either a press or a swipe (a swipe starting on the toolbar still steers), never both.
 */
export const HUD_TAP_MOVE = DEFAULT_GESTURES.tapMaxMove;

/** A text button. Fires on pointer release over the text (taps and clicks). */
export function button(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  onPress: () => void,
  scale: TextScale = 1,
  color: number = UI.text,
  /** Fire only on a tap ({@link onTap}): for buttons over the lawn, where swipes go. */
  tapOnly = false,
): Phaser.GameObjects.BitmapText {
  const t = label(scene, x, y, text, scale, color);
  // Pad the hit area so small pixel text is still easy to tap.
  const pad = 4;
  t.setInteractive({
    hitArea: new Phaser.Geom.Rectangle(-pad, -pad, t.width + 2 * pad, t.height + 2 * pad),
    hitAreaCallback: (area: Phaser.Geom.Rectangle, px: number, py: number) => area.contains(px, py),
    useHandCursor: true,
  });
  const press = () => {
    gameAudio.play('click');
    onPress();
  };
  if (tapOnly) onTap(t, press, HUD_TAP_MOVE);
  else t.on('pointerup', press);
  return t;
}

/**
 * A centered message panel (background box + wrapped, centered text), fixed to the camera.
 * Returns a container; call `setMessage` to change its text.
 */
export class MessagePanel {
  private readonly box: Phaser.GameObjects.Rectangle;
  private readonly text: Phaser.GameObjects.BitmapText;
  readonly container: Phaser.GameObjects.Container;

  constructor(
    private readonly scene: Phaser.Scene,
    private maxWidth: number,
  ) {
    this.box = scene.add.rectangle(0, 0, 10, 10, UI.panel, UI.panelAlpha).setOrigin(0.5);
    this.box.setStrokeStyle(1, UI.dim, 0.6);
    // Top-left origin, placed on whole pixels in setMessage (a centered origin on odd-width text
    // lands on half pixels and smears the glyphs).
    this.text = label(scene, 0, 0, '').setCenterAlign().setOrigin(0, 0);
    this.container = scene.add.container(0, 0, [this.box, this.text]).setScrollFactor(0);
  }

  setMessage(message: string, x: number, y: number, maxWidth = this.maxWidth): this {
    this.maxWidth = maxWidth;
    this.text.setMaxWidth(maxWidth - 12).setText(message);
    const w = Math.ceil(this.text.width) + 12;
    const h = Math.ceil(this.text.height) + 10;
    // Even sizes keep the centered box and text on whole pixels.
    this.box.setSize(w + (w % 2), h + (h % 2));
    this.text.setPosition(-Math.floor(this.text.width / 2), -Math.floor(this.text.height / 2));
    this.container.setPosition(Math.round(x), Math.round(y)).setVisible(true);
    this.scene.children.bringToTop(this.container);
    return this;
  }

  setVisible(visible: boolean): this {
    this.container.setVisible(visible);
    return this;
  }

  get visible(): boolean {
    return this.container.visible;
  }

  setDepth(depth: number): this {
    this.container.setDepth(depth);
    return this;
  }
}
